import { ref } from "vue";
import Swal from "sweetalert2";
import api from "@/servicios/apiCliente";
import { Core } from "@/servicios/api";

const TAMANIO_MAXIMO_ARCHIVO = 10 * 1024 * 1024;
const EXTENSIONES_PERMITIDAS = ["pdf", "png", "jpg"];

/**
 * Flujo de aclaraciones del home (extraído de CCHome.vue).
 *
 * Pasos: 1) elegir tipo de aclaración, 2) describir y adjuntar evidencia,
 * 3) mostrar el folio generado.
 */
export function useAclaraciones() {
  const abierto = ref(false);
  const paso = ref(0);

  const aclaraciones = ref([] as Core.Models.TipoAclaracion[]);
  const crearAclaracion = ref({} as Core.Models.Aclaracion);
  const aclaracionCliente = ref({} as Core.Models.AclaracionUsuario);

  const descripcion = ref("");
  const files = ref([] as File[]);
  const folio = ref({} as any);

  const mostrarOpcionAclaracion = ref(true);
  const mostrarDescripcion = ref(false);
  const mostrarFolio = ref(false);

  const avisoAclaracion = ref(false);
  const avisoDescripcion = ref(false);
  const mensajeTamanioArchivo = ref(false);
  const mensajeTipoArchivo = ref(false);

  async function cargarTiposAclaracion() {
    const resp = await api.Cliente.Cliente.AclaracionApi.ListarAclaraciones();
    aclaraciones.value = resp.data;
  }

  function seleccionarAclaracion(aclaracion: any) {
    crearAclaracion.value.tipoAclaracionId = aclaracion.tipoAclaracionId;
    crearAclaracion.value.tipoAclaracion = aclaracion;
  }

  /** Avanza el flujo. Replica la máquina de estados de CCHome.vue. */
  async function avanzar() {
    paso.value += 1;

    if (paso.value === 1) {
      if (!crearAclaracion.value.tipoAclaracionId) {
        mostrarDescripcion.value = false;
        paso.value = 0;
        avisoAclaracion.value = true;
        return;
      }
      mostrarDescripcion.value = true;
      avisoAclaracion.value = false;
      return;
    }

    if (paso.value !== 2) return;

    if (!descripcion.value.length) {
      avisoDescripcion.value = true;
      paso.value = 1;
      return;
    }

    avisoDescripcion.value = false;
    mensajeTamanioArchivo.value = false;
    mostrarOpcionAclaracion.value = false;
    mostrarFolio.value = true;
    crearAclaracion.value.detalle = descripcion.value;

    const aclaracionGuardada = (
      await api.Cliente.Cliente.AclaracionApi.GuardarAclaracion(crearAclaracion.value)
    ).data;
    folio.value = aclaracionGuardada.data;
    aclaracionCliente.value.aclaracionId = folio.value.aclaracionId;
    aclaracionCliente.value.aclaracion = folio.value.aclaracion;

    const archivos = [] as string[];
    for (const archivo of files.value) {
      const guardado = await api.Cliente.Cliente.AclaracionApi.GuardarArchivo(archivo, folio.value.ruta!);
      archivos.push(guardado.data);
    }
    await api.Cliente.Cliente.AclaracionApi.CrearZip(archivos.toString());
    await api.Cliente.Cliente.AclaracionApi.GuardarUsuarioAclaracion(aclaracionCliente.value);
  }

  function agregarArchivos(event: Event) {
    const seleccionados = (event.target as HTMLInputElement).files;
    if (!seleccionados) return;

    for (const archivo of Array.from(seleccionados)) {
      if (archivo.size > TAMANIO_MAXIMO_ARCHIVO) mensajeTamanioArchivo.value = true;

      const extension = archivo.type.split("/")[1];
      mensajeTipoArchivo.value = !EXTENSIONES_PERMITIDAS.includes(extension);

      files.value.push(archivo);
    }
    crearAclaracion.value.archivo = true;
  }

  function eliminarArchivo(indice: number) {
    files.value.splice(indice, 1);
  }

  function visualizarArchivo(archivo: Blob) {
    return URL.createObjectURL(archivo);
  }

  /**
   * Abre el modal, salvo que el cliente tenga coberturas vencidas: en ese
   * caso se le redirige a WhatsApp con la instrucción correspondiente.
   */
  async function abrir() {
    const pagosVencidos = (await api.Cliente.Cliente.HomeApi.ObtenerAdeudoVencidos()).data.data as any;

    let mensaje = "";
    if (
      (pagosVencidos.esIntegrante || pagosVencidos.esAdjudicatario) &&
      pagosVencidos.cuotasVencidas > 0 &&
      pagosVencidos.cuotasVencidas <= 3.5
    ) {
      mensaje = "Da clic en opción 1 seguido de la opción A.";
    } else if (
      (pagosVencidos.esAdjudicatario && pagosVencidos.cuotasVencidas >= 3.51) ||
      (pagosVencidos.esAdjudicado && pagosVencidos.cuotasVencidas >= 0.01)
    ) {
      mensaje = "Da clic en opción 3.";
    }

    if (mensaje) {
      Swal.fire({
        html: `<p class="fs-2 fw-bold">Estimado cliente:</p></br>
               <p class="h6">Por el momento tu(s) cobertura(s) en Seguro de Vida y Daños no se encuentran vigentes.</p></br>
               <p class="h6 fw-bold">Te sugerimos verificarlo.</p></br>
               <div class="card">
                 <div class="card-body" style="background-color: #eceff2">
                   <a href="${pagosVencidos.contactoWhatsApp}" target="_blank" class="btn btn-primary text-white">Contáctanos vía WhatsApp</a></br>
                   <div style="border-right: solid; width: 50%; height:20px"></div>
                   <p class="h6">${mensaje}</p></br>
                 </div>
               </div>`,
        showCloseButton: true,
        showConfirmButton: false
      });
      return;
    }

    abierto.value = true;
  }

  function cerrar() {
    abierto.value = false;
    paso.value = 0;
    crearAclaracion.value.tipoAclaracionId = 0;
    crearAclaracion.value.archivo = false;
    descripcion.value = "";
    files.value = [];
    mostrarOpcionAclaracion.value = true;
    mostrarDescripcion.value = false;
    mostrarFolio.value = false;
    avisoAclaracion.value = false;
    avisoDescripcion.value = false;
    mensajeTipoArchivo.value = false;
    mensajeTamanioArchivo.value = false;
  }

  return {
    abierto,
    aclaraciones,
    crearAclaracion,
    descripcion,
    files,
    folio,
    mostrarOpcionAclaracion,
    mostrarDescripcion,
    mostrarFolio,
    avisoAclaracion,
    avisoDescripcion,
    mensajeTamanioArchivo,
    mensajeTipoArchivo,
    cargarTiposAclaracion,
    seleccionarAclaracion,
    avanzar,
    agregarArchivos,
    eliminarArchivo,
    visualizarArchivo,
    abrir,
    cerrar
  };
}
